import { AxiosPromise } from 'axios';

import {
  BaseRepository,
  IEnumerableResponse,
  ITableRequestParams
} from '@app/src/common/repository/BaseRepository';
import {
  IRoom,
  IRoomForm,
  IRooms
} from '@app/src/components/booking-management/model/Rooms';
import {
  IMeeting,
  IMeetingInList,
  IRoomWithMeetings,
  IRoomWithMeetingsParams
} from '@app/src/components/booking-management/model/Meetings';
import {
  ILocation,
  ILocationFormValues,
  ILocations
} from '@app/src/components/booking-management/model/Locations';
import {
  ICreateResourceDto,
  IResource,
  IUpdateResourceDto
} from '@app/src/components/booking-management/model/Resources';
import { ISettings } from '../model/Settings';
import { ICategory } from '@app/src/components/booking-management/model/Category';
import { INamedItem } from '@app/src/common/model/ListModel';

interface IMeetingParams {
  includeDeleted?: boolean;
  fillModifiedByFullName?: boolean;
}

class BookingRepository extends BaseRepository {
  roomsUrl = `${this.roomBookingUrl}/rooms`;
  locationsUrl = `${this.roomBookingUrl}/locations`;
  meetingsUrl = `${this.roomBookingUrl}/meetings`;
  resourcesUrl = `${this.roomBookingUrl}/resources`;
  categoriesUrl = `${this.roomBookingUrl}/categories`;
  settingsUrl = `${this.roomBookingUrl}/settings`;

  public getRooms(params?: ITableRequestParams): AxiosPromise<IRooms> {
    return this.callGet<IRooms>(`${this.roomsUrl}`, params);
  }

  public getRoomNames(): Promise<INamedItem[]> {
    return this.callGet<INamedItem[]>(`${this.roomsUrl}/names`).then(
      result => result.data
    );
  }

  public updateRoom(params: IRoomForm): AxiosPromise<IRooms> {
    return this.callPut<IRooms>(`${this.roomsUrl}/${params.id}`, params);
  }

  public createRoom(params: IRoomForm): AxiosPromise<IRooms> {
    return this.callPost<IRooms>(`${this.roomsUrl}`, params);
  }

  public getRoom(id: string): AxiosPromise<IRoom> {
    return this.callGet<IRoom>(`${this.roomsUrl}/${id}`);
  }

  public syncRoom(id: string): AxiosPromise<void> {
    return this.callPut<void>(`${this.roomsUrl}/${id}/sync`, {});
  }

  public getLocations(params?: ITableRequestParams): AxiosPromise<ILocations> {
    return this.callGet<ILocations>(`${this.locationsUrl}`, params);
  }

  public getLocation(id: string): AxiosPromise<ILocation> {
    return this.callGet<ILocation>(`${this.locationsUrl}/${id}`);
  }

  public updateLocation(params: ILocationFormValues): AxiosPromise<ILocation> {
    return this.callPut<ILocation>(`${this.locationsUrl}/${params.id}`, params);
  }

  public createLocation(params: ILocationFormValues): AxiosPromise<ILocation> {
    return this.callPost<ILocation>(`${this.locationsUrl}`, params);
  }

  public getMeetings(
    params?: ITableRequestParams
  ): AxiosPromise<IEnumerableResponse<IMeetingInList>> {
    return this.callPost<IEnumerableResponse<IMeetingInList>>(
      `${this.meetingsUrl}/list`,
      params
    );
  }

  public async getMeeting(
    id: string,
    params: IMeetingParams
  ): Promise<IMeeting> {
    const result = await this.callGet<IMeeting>(
      `${this.meetingsUrl}/${id}`,
      params
    );
    return result.data;
  }

  public downloadMeeting(format: number, params?: ITableRequestParams) {
    const url = `${this.meetingsUrl}/download${this.constructHashString({ format })}`;
    return this.callPost<ArrayBuffer>(url, params, {
      responseType: 'arraybuffer'
    }).then(result => result.data);
  }

  public getRoomsWithMeetings(
    params?: IRoomWithMeetingsParams
  ): Promise<IRoomWithMeetings[]> {
    return this.callGet<IRoomWithMeetings[]>(
      `${this.roomsUrl}/meetings`,
      params
    ).then(result => result.data);
  }

  public getRoomsTags(): Promise<{ values: string[] }> {
    return this.callGet<{ values: string[] }>(`${this.roomsUrl}/tags`).then(
      result => result.data
    );
  }

  public getResources(
    params?: ITableRequestParams
  ): Promise<IEnumerableResponse<IResource>> {
    return this.callGet<IEnumerableResponse<IResource>>(
      `${this.resourcesUrl}`,
      params
    ).then(result => result.data);
  }

  public getResource(id: string): Promise<IResource> {
    return this.callGet<IResource>(`${this.resourcesUrl}/${id}`).then(
      result => result.data
    );
  }

  public createResource(body: ICreateResourceDto) {
    return this.callPost(`${this.resourcesUrl}`, body);
  }

  public updateResource(id: string, body: IUpdateResourceDto) {
    return this.callPut(`${this.resourcesUrl}/${id}`, body);
  }

  public getCategories(
    params?: ITableRequestParams
  ): Promise<IEnumerableResponse<ICategory>> {
    return this.callGet<IEnumerableResponse<ICategory>>(
      `${this.categoriesUrl}`,
      params
    ).then(result => result.data);
  }

  public getCategory(id: string): Promise<ICategory> {
    return this.callGet<IResource>(`${this.categoriesUrl}/${id}`).then(
      result => result.data
    );
  }

  public createCategory(body: ICategory) {
    return this.callPost(`${this.categoriesUrl}`, body);
  }

  public updateCategory(id: string, body: ICategory) {
    return this.callPut(`${this.categoriesUrl}/${id}`, body);
  }

  public getSettings() {
    return this.callGet<ISettings>(this.settingsUrl).then(
      result => result.data
    );
  }

  public updateSettings(settings: ISettings) {
    return this.callPut(this.settingsUrl, settings);
  }

  public createMeeting(meeting: IMeeting): Promise<string> {
    return this.callPost<string>(`${this.meetingsUrl}`, meeting).then(
      result => result.data
    );
  }

  public updateMeeting(meeting: IMeeting) {
    return this.callPut(`${this.meetingsUrl}/${meeting.id}`, meeting);
  }

  public cancelMeeting(id: string) {
    return this.callPut(`${this.meetingsUrl}/${id}/cancel`, {});
  }

  public bookedMeeting(id: string) {
    return this.callPut(`${this.meetingsUrl}/${id}/booked`, {});
  }

  public retryPostponeMeeting(id: string) {
    return this.callPost(`${this.meetingsUrl}/${id}/postponed-operation/retry`);
  }
}

export default new BookingRepository();
